/**
 * 페이징 및 무한스크롤 전용 Composable
 * 목록 데이터와 페이징 상태를 관리
 */

import { ref, readonly } from "vue";
import { useApiBase } from "./useApiBase.js";

/**
 * @typedef {Object} PaginationOptions
 * @property {string} domain - 도메인 이름
 * @property {string} endpoint - API 엔드포인트
 * @property {Object} [defaultParams] - 기본 검색 파라미터
 * @property {number} [defaultSize=24] - 기본 페이지 크기
 * @property {string} [defaultSort='createdAt'] - 기본 정렬 필드
 * @property {string} [defaultDirection='desc'] - 기본 정렬 방향
 */

/**
 * 페이징과 무한스크롤을 지원하는 Composable
 * @param {PaginationOptions} options - 설정 옵션
 * @returns {Object} 페이징 관련 상태와 메서드들
 */
export function usePagination(options = {}) {
  const {
    domain,
    endpoint,
    defaultParams = {},
    defaultSize = 24,
    defaultSort = "createdAt",
    defaultDirection = "desc",
  } = options;

  if (!domain || !endpoint) {
    throw new Error("domain과 endpoint는 필수입니다");
  }

  // API 기본 기능 가져오기
  const {
    loading,
    error,
    executeApiCall,
    updateArrayData,
    validatePaginationParams,
    clearCache: clearApiCache,
    reset: resetApi,
  } = useApiBase({ domain });

  // 페이징 관련 상태
  const items = ref([]);
  const currentPageData = ref(null);

  /**
   * 기본 파라미터 생성
   * @param {Object} params - 사용자 파라미터
   * @returns {Object} 최종 파라미터
   */
  const buildParams = (params = {}) => {
    // direction을 대문자로 변환 (Spring Boot Sort.Direction enum 호환)
    const normalizeDirection = (direction) => {
      if (!direction) return 'ASC';
      return direction.toString().toUpperCase();
    };

    const finalParams = {
      q: "",
      page: 0,
      size: defaultSize,
      sort: defaultSort,
      direction: normalizeDirection(defaultDirection),
      ...defaultParams,
      ...params,
    };

    // params에서 전달된 direction도 정규화
    if (finalParams.direction) {
      finalParams.direction = normalizeDirection(finalParams.direction);
    }

    return finalParams;
  };

  /**
   * 데이터 조회 (페이징 + 무한스크롤 지원)
   * @param {Object} params - 검색 파라미터
   * @param {boolean} [append=false] - 기존 목록에 추가할지 여부
   * @returns {Promise<Object>} 페이징된 데이터
   */
  const fetchItems = async (params = {}, append = false) => {
    const finalParams = buildParams(params);

    // 파라미터 검증
    validatePaginationParams(finalParams);

    // API 호출 (append 모드에서는 캐시 비활성화)
    const data = await executeApiCall(endpoint, finalParams, {
      cache: !append || finalParams.page === 0,
      validateParams: validatePaginationParams,
    });

    // 상태 업데이트
    updateArrayData(items, data, append);
    currentPageData.value = data;

    return data;
  };

  /**
   * 검색 (첫 페이지부터)
   * @param {Object} searchParams - 검색 파라미터
   * @returns {Promise<Object>} 검색 결과
   */
  const search = async (searchParams = {}) => {
    return fetchItems({ ...searchParams, page: 0 }, false);
  };

  /**
   * 다음 페이지 로드 (무한스크롤)
   * @param {Object} baseParams - 기본 검색 파라미터
   * @returns {Promise<Object>} 다음 페이지 데이터
   */
  const loadMore = async (baseParams = {}) => {
    const currentPage = currentPageData.value?.pageable?.pageNumber ?? 0;
    const nextPage = currentPage + 1;

    return fetchItems({ ...baseParams, page: nextPage }, true);
  };

  /**
   * 특정 페이지로 이동
   * @param {number} page - 페이지 번호
   * @param {Object} baseParams - 기본 검색 파라미터
   * @returns {Promise<Object>} 페이지 데이터
   */
  const goToPage = async (page, baseParams = {}) => {
    return fetchItems({ ...baseParams, page }, false);
  };

  /**
   * 캐시 초기화
   */
  const clearCache = () => {
    clearApiCache();
  };

  /**
   * 모든 상태 초기화
   */
  const reset = () => {
    items.value = [];
    currentPageData.value = null;
    resetApi();
  };

  return {
    // 읽기 전용 상태
    items: readonly(items),
    loading,
    error,
    currentPageData: readonly(currentPageData),

    // 액션
    fetchItems,
    search,
    loadMore,
    goToPage,
    clearCache,
    reset,
  };
}
